/**
 * This file contains the types for all events communicated between the FinancialPartnerPayButton
 * and the express checkout element in the parent frame.
 */
import {ChildTypes} from './types';

export type FinancialPartnerChildTypes = Extract<
  ChildTypes,
  'DemoPayButton' | 'AmazonPayButton' | 'ShopPayButton'
>;

// Inbound Events
export type FinancialPartnerLoadButtonEvent = {
  event: 'loadButton';
  payButtonOptions: PayButtonOptions;
  isNativeMobile?: boolean;
  typeSpecificData?: FinancialPartnerLoadButtonEventTypeSpecificData;
};

export type FinancialPartnerLoadButtonEventTypeSpecificData = {
  shopPay?: {
    shopId: string;
  };
};

export type NativeMobileClickEvent = {
  event: 'NativeMobileClicked';
} & CommonClickPayload;

export type NativeMobileClickResponse = {
  event: 'NativeMobileClickResponse';
  isPopupOpen?: boolean;
};

export type FinancialPartnerOnBeforePayButtonDisplayResponseEvent = {
  event: 'OnBeforePayButtonDisplayResponse';
  shouldDisplayButton: boolean;
};

export function isFinancialPartnerOnBeforePayButtonDisplayResponseEvent(
  type: FinancialPartnerPayButtonInboundPayload | undefined,
): type is FinancialPartnerOnBeforePayButtonDisplayResponseEvent {
  return type?.event === 'OnBeforePayButtonDisplayResponse';
}

export type SetStateOptions = {
  isDisabled: boolean;
  height: number;
  locale: string;
};

export type FinancialPartnerPayButtonSetStateEvent = {
  event: 'setButtonState';
  options: SetStateOptions;
};

/**
 * When received, we will throw a partner-visible FinancialPartnerIntegrationError with the given error/message.
 */
export type FinancialPartnerIntegrationErrorResponse = {
  event: 'PartnerIntegrationErrorResponse';
  message?: string;
  code?: string;
};

/**
 * When received, we will throw a partner-visible MerchantIntegrationError with
 * the given message to notify them of merchant integration issues.
 */
export type MerchantIntegrationErrorResponse = {
  event: 'MerchantIntegrationErrorResponse';
  message?: string;
  keepAlive?: boolean;
};

export type FinancialPartnerPayButtonClickedResponseTypeSpecificData = {
  shopPay?: {
    checkoutUrl: string;
  };
};

export type FinancialPartnerPayButtonClickedResponse = {
  event: 'ClickedResponse';
} & CommonClickPayload;

export type CommonClickPayload = {
  stripeExpressSession: string;
  stripeExpressSessionSignature: string;
  checkoutState: CheckoutStateWithCurrency;
  options: {
    allowedShippingCountries?: string[];
    requiredFields?: {
      billingAddress?: boolean;
      email?: boolean;
      phoneNumber?: boolean;
      shippingAddress?: boolean;
    };
  };
  displayData: {
    name: string;
    logoUrl?: string;
  };
  typeSpecificData?: FinancialPartnerPayButtonClickedResponseTypeSpecificData;
};

export type PayButtonOptions = {
  style: {
    pageTheme: 'light' | 'dark';
    buttonType: 'pay' | 'buy' | 'checkout';
    height: number;
  };
  cardBrands:
    | {type: 'allowed'; allowedBrands: string[]}
    | {type: 'disallowed'; disallowedBrands: string[]};
  amount?: number;
  currency?: string;
  locale: string;
  livemode: boolean;
};

export type LineItem = {
  name: string;
  amount: number;
};

export type PartialAddress = {
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

export type ShippingRate = {
  id: string;
  amount: number;
  displayName: string;
  deliveryEstimate?: string;
};

export type DeliveryEstimate = {
  unit: DeliveryUnit;
  value: number;
};

export type CheckoutStateWithCurrency = CheckoutState & {currency?: string};

export type CheckoutState = {
  amount?: number;
  lineItems?: LineItem[];
  shippingRates?: ShippingRate[];
};

export type DeliveryUnit = 'hour' | 'day' | 'business_day' | 'week' | 'month';

export type BillingChangedResponse = {
  event: 'BillingChangedResponse';
  checkoutState: CheckoutState;
  error?: {
    reason: string;
    message: string;
  };
};

export type ShippingChangedResponse = {
  event: 'ShippingChangedResponse';
  merchantDecision: 'accepted' | 'rejected';
  checkoutState: CheckoutState;
  error?: {
    reason: string;
    message: string;
  };
};

export type ConfirmedResponse = {
  event: 'ConfirmedResponse';
  status: 'succeeded' | 'failed';
  error?: {
    reason: string;
    message: string;
  };
};

type RedirectCheckoutInterface = {
  event: 'RedirectCheckoutInterface';
  /** E.g. the 3DS URL */
  authenticationUrl: string;
  /** When this flag is enabled, do not emit `onBeforeRedirect` to the partner and redirect the popup window ourselves instead. This will be used when
   * `redirectedByPartner` returns false.
   */
  bypassPartnerRedirect?: boolean;
};

/**
 * Sent from sjs => stripethirdparty when the buyers session has expired.
 */
type SessionExpired = {
  event: 'SessionExpired';
};

export type Abort = {
  event: 'abort';
};

export type Refocus = {
  event: 'refocus';
};
// events sent from ECE -> StripeThirdParty
export type FinancialPartnerPayButtonInboundPayload =
  | FinancialPartnerLoadButtonEvent
  | NativeMobileClickEvent
  | FinancialPartnerOnBeforePayButtonDisplayResponseEvent
  | FinancialPartnerPayButtonSetStateEvent
  | FinancialPartnerPayButtonClickedResponse
  | BillingChangedResponse
  | ShippingChangedResponse
  | ConfirmedResponse
  | RedirectCheckoutInterface
  | FinancialPartnerIntegrationErrorResponse
  | MerchantIntegrationErrorResponse
  | SessionExpired
  | Refocus
  | Abort;

// Outbound Events
export type FinancialPartnerOnBeforeButtonDisplayEvent = {
  event: 'OnBeforePayButtonDisplay';
};

export type FinancialPartnerPayButtonClickedEvent = {
  event: 'Clicked';
};

export type PopupReadyEvent = {
  event: 'ReadyForBuyerInteraction';
};

export type BillingAddressSelectedEvent = {
  event: 'BillingAddressSelected';
  billingAddress: PartialAddress;
};

export type ShippingAddressSelectedEvent = {
  event: 'ShippingAddressSelected';
  shippingAddress: PartialAddress;
};

export type ShippingRateSelectedEvent = {
  event: 'ShippingRateSelected';
  shippingRate: ShippingRate;
};

export type ConfirmEventTypeSpecificData = {
  shopPay?: {
    // deprecated! keeping for backward compatibility of types
    paymentMethodToken?: string;
    externalSourceId: string;
  };
};

export type ConfirmedEvent = {
  event: 'Confirmed';
  paymentMethodOptions?: ConfirmEventTypeSpecificData;
};

export type CanceledEvent = {
  event: 'Canceled';
};

type ErrorEventTypeNoPrefix =
  | 'set_button_state'
  | 'redirect_checkout_interface'
  | 'button_mount'
  | 'script_load'
  | 'native_mobile_clicked';

export type ErrorEventType = `stripethirdparty_${ErrorEventTypeNoPrefix}`;

export type ErrorEvent = {
  event: 'Error';
  errorType?: ErrorEventType;
  error?: string;
  eventType?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  errorObj?: Error;
};

export type LogInfoEvent = {
  event: 'Info';
  message: string;
  data?: Record<string, unknown>;
};

/** When the partner calls sendError, we will close the popup and report the
 * event back to sjs for metrics and logging. */
export type FatalErrorEvent = {
  event: 'FatalErrorEvent';
  message?: string;
  code?: string;
  keepAlive?: boolean;
};

export type RedirectCheckoutInterfaceResponse = {
  event: 'RedirectCheckoutInterfaceResponse';
  redirectedByPartner: boolean;
};

export type FocusEvent = {
  event: 'onFocus';
};

export type BlurEvent = {
  event: 'onBlur';
};

// events being sent from StripeThirdParty -> ECE
export type FinancialPartnerPayButtonOutboundPayload =
  | FinancialPartnerOnBeforeButtonDisplayEvent
  | FinancialPartnerPayButtonClickedEvent
  | PopupReadyEvent
  | BillingAddressSelectedEvent
  | ShippingAddressSelectedEvent
  | ShippingRateSelectedEvent
  | ConfirmedEvent
  | CanceledEvent
  | ErrorEvent
  | LogInfoEvent
  | FatalErrorEvent
  | RedirectCheckoutInterfaceResponse
  | NativeMobileClickResponse
  | FocusEvent
  | BlurEvent;
